import { DateTime } from "luxon";
import type {
  CallFollowUp,
  FollowUpRequestType,
  FollowUpResolvedVia
} from "./types";

// Only generic names: nothing the AI could have misheard (unit, date, time)
// ever reaches a system-written label (AC.3).
export const FOLLOW_UP_SHORT_LABEL: Record<FollowUpRequestType, string> = {
  showing_requested: "Showing requested",
  asked_for_callback: "Asked for a callback",
  question_not_answered: "Question not answered",
  asked_for_person: "Asked for a person"
};

const FOLLOW_UP_REQUEST_SENTENCE: Record<FollowUpRequestType, string> = {
  showing_requested: "Asked about a showing",
  asked_for_callback: "Asked for a callback",
  question_not_answered: "Asked a question the agent could not answer",
  asked_for_person: "Asked for a person"
};

const RESOLVED_VIA_LABEL: Record<FollowUpResolvedVia, string> = {
  sms: "SMS",
  email: "email",
  call: "phone",
  manual: "Status"
};

const lowerFirst = (value: string) =>
  value.charAt(0).toLowerCase() + value.slice(1);

const followUpHeadline = (
  followUp: Pick<CallFollowUp, "request_types">
): FollowUpRequestType | null => followUp.request_types[0] ?? null;

export const followUpShortLabel = (
  followUp: Pick<CallFollowUp, "request_types">
): string => {
  const headline = followUpHeadline(followUp);
  return headline ? FOLLOW_UP_SHORT_LABEL[headline] : "Needs follow-up";
};

export const followUpSecondaryLabels = (
  followUp: Pick<CallFollowUp, "request_types">
): string[] =>
  followUp.request_types.slice(1).map((type) => FOLLOW_UP_SHORT_LABEL[type]);

export const followUpSentence = (
  followUp: Pick<CallFollowUp, "request_types">
): string => {
  const headline = followUpHeadline(followUp);
  return headline ? FOLLOW_UP_REQUEST_SENTENCE[headline] : "Needs follow-up";
};

// "asked about a showing", for sentences such as "Caller asked about a showing."
export const followUpSentenceLower = (
  followUp: Pick<CallFollowUp, "request_types">
): string => lowerFirst(followUpSentence(followUp));

export const isFollowUpOpen = (
  followUp: CallFollowUp | null | undefined
): followUp is CallFollowUp => followUp?.status === "open";

export const formatFollowUpTime = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const parsed = DateTime.fromISO(iso);
  return parsed.isValid ? parsed.toFormat("h:mm a") : "";
};

// "3:05 PM CST"
export const formatFollowUpTimeWithZone = (
  iso: string | null | undefined
): string => {
  if (!iso) return "";
  const parsed = DateTime.fromISO(iso);
  return parsed.isValid ? parsed.toFormat("h:mm a ZZZZ") : "";
};

// "today", "yesterday" or "on Mar 4", lower-cased for mid-sentence use.
export const formatFollowUpDay = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const parsed = DateTime.fromISO(iso);
  if (!parsed.isValid) return "";
  const today = DateTime.now().startOf("day");
  const day = parsed.startOf("day");
  if (day.equals(today)) return "today";
  if (day.equals(today.minus({ days: 1 }))) return "yesterday";
  return `on ${parsed.toFormat("LLL d")}`;
};

// "replied by SMS" / "called back" / "resolved it"
export const followUpActionLabel = (
  via: FollowUpResolvedVia | null | undefined
): string => {
  if (via === "call") return "called back";
  if (via === "manual" || !via) return "resolved it";
  return `replied by ${RESOLVED_VIA_LABEL[via]}`;
};

// "Dana Whitfield replied by SMS at 3:40 PM"
export const followedUpSummary = (followUp: CallFollowUp): string => {
  const who = followUp.followed_up_by?.trim() || "A teammate";
  const time = formatFollowUpTime(followUp.followed_up_at);
  const action = followUpActionLabel(followUp.followed_up_via);
  return time ? `${who} ${action} at ${time}` : `${who} ${action}`;
};

// "Replied by SMS at 3:40 PM"
export const followedUpActionSentence = (followUp: CallFollowUp): string => {
  const action = followUpActionLabel(followUp.followed_up_via);
  const time = formatFollowUpTime(followUp.followed_up_at);
  const capitalized = action.charAt(0).toUpperCase() + action.slice(1);
  return time ? `${capitalized} at ${time}` : capitalized;
};
